import React, { useMemo } from "react";
import Modal from "../ui/Modal";
import {
  Building2,
  MapPin,
  Award,
  LayoutTemplate,
  Waves,
  Dumbbell,
  Baby,
  Shield,
  Cpu,
  Leaf,
  Users,
  PartyPopper,
  Wrench,
  GraduationCap,
  Briefcase,
  HeartPulse,
  Bus,
  Clapperboard,
  CheckCircle2,
  XCircle,
  MinusCircle,
  BadgeCheck,
  Sparkles,
} from "lucide-react";

import "../../styles/PropertyDetails.css";
import PropertyDetailsAccordian from "./PropertyDetailsAccordian";

/* ─── stable empty refs — avoid new [] / {} identities on every render ─── */
const EMPTY_ARR = [];
const EMPTY_OBJ = {};

/* ─── helpers ─── */

const toTitleCase = (str) => {
  if (!str) return "—";

  return String(str)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const CATEGORY_ICONS = {
  fitness: HeartPulse,
  swimming: Waves,
  gym: Dumbbell,
  sports: Award,
  entertainment: PartyPopper,
  kids: Baby,
  community: Users,
  green_space: Leaf,
  security: Shield,
  technology: Cpu,
  default: Wrench,
};

function toCategoryList(categories) {
  if (Array.isArray(categories)) return categories.filter(Boolean);
  if (typeof categories === "string" && categories.trim()) {
    return categories
      .split(/[,|]/)
      .map((c) => c.trim())
      .filter(Boolean);
  }
  return EMPTY_ARR;
}

function amenityFields(amenity) {
  if (amenity == null) {
    return { name: "", categories: EMPTY_ARR };
  }
  if (typeof amenity === "string") {
    return { name: amenity, categories: EMPTY_ARR };
  }
  return {
    name: amenity.name ?? amenity.title ?? amenity.amenity ?? amenity.label ?? "",
    categories: toCategoryList(amenity.categories ?? amenity.category),
  };
}

function iconFor(categories = EMPTY_ARR, name) {
  const lowerName = String(name || "").toLowerCase();
  const cats = toCategoryList(categories);

  if (lowerName === "gym") {
    return CATEGORY_ICONS.gym;
  }
  if (lowerName.includes("pool") || lowerName.includes("swimming")) {
    return CATEGORY_ICONS.swimming;
  }
  for (const cat of cats) {
    const key = String(cat).toLowerCase().replace(/\s+/g, "_");
    if (CATEGORY_ICONS[key]) {
      return CATEGORY_ICONS[key];
    }
  }
  return CATEGORY_ICONS.default;
}

/* memoized so re-renders of the parent don't re-render every chip/icon
   unless its own props actually changed */
const AmenityIcon = React.memo(function AmenityIcon({ categories = EMPTY_ARR, name }) {
  const Icon = iconFor(categories, name) || CATEGORY_ICONS.default;
  return <Icon size={13} style={ICON_STYLE} />;
});

const LANDMARK_ICONS = {
  schools: GraduationCap,
  it_parks: Briefcase,
  hospitals: HeartPulse,
  transport: Bus,
  entertainment: Clapperboard,
};

const LandmarkIcon = React.memo(function LandmarkIcon({ category }) {
  const Icon = LANDMARK_ICONS[category] || MapPin;
  return <Icon size={13} style={ICON_STYLE} />;
});

const ApprovalIcon = React.memo(function ApprovalIcon({ value }) {
  if (value === true)
    return <CheckCircle2 size={18} style={APPROVAL_ICON_HOT_STYLE} />;
  if (value === false)
    return <XCircle size={18} style={APPROVAL_ICON_MUTED_STYLE} />;
  return <MinusCircle size={18} style={APPROVAL_ICON_MUTED_STYLE} />;
});

function approvalText(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not specified";
}

/* ─── shared primitives (all style objects hoisted to module scope —
   these were previously recreated on every single render) ─── */

const styles = {
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--color-medium-gray)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: "0.75rem",
  },
  sectionIcon: {
    color: "var(--color-light-blue)",
  },
  card: {
    background: "var(--color-white)",
    border: "1px solid var(--color-border)",
    borderRadius: 10,
    padding: "0.75rem 1rem",
  },
  label: {
    fontSize: 11,
    color: "var(--color-medium-gray)",
    fontWeight: 500,
    marginBottom: 4,
  },
  value: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--color-deep-navy)",
  },
  valueSmall: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-deep-navy)",
  },
  valueSmallMuted: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-medium-gray)",
  },
  divider: {
    height: 1,
    background: "var(--color-border)",
    margin: "0.25rem 0",
  },
};

const ICON_STYLE = { color: "var(--color-light-blue)" };
const APPROVAL_ICON_HOT_STYLE = { color: "var(--color-hot)" };
const APPROVAL_ICON_MUTED_STYLE = { color: "var(--color-medium-gray)" };
const RERA_LABEL_ICON_STYLE = { color: "var(--color-medium-gray)" };

const APPROVAL_FIELDS = [
  { label: "CMDA approved", key: "cmda_approved" },
  { label: "DTCP approved", key: "dtcp_approved" },
  { label: "Bank loan", key: "bank_loan_available" },
];

const SectionTitle = React.memo(function SectionTitle({ icon: Icon, children }) {
  return (
    <div style={styles.sectionTitle}>
      {Icon && <Icon size={14} style={styles.sectionIcon} />}
      {children}
    </div>
  );
});

const StatCard = React.memo(function StatCard({ label, value }) {
  return (
    <div style={styles.card}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{value || "—"}</div>
    </div>
  );
});

function Divider() {
  return <div style={styles.divider} />;
}

/* ─── main component ─── */

function PropertyDetails({ PropertyDetails, isOpen, onClose }) {

  const {
    amenities: rawAmenities = EMPTY_ARR,
    approvals = EMPTY_OBJ,
    highlights = EMPTY_ARR,
    building_details = EMPTY_OBJ,
    nearby_landmarks = EMPTY_OBJ,
    apartment_size_sqft = EMPTY_OBJ,
    unit_size_ranges = EMPTY_ARR,
  } = PropertyDetails || EMPTY_OBJ;

  const amenities = Array.isArray(rawAmenities) ? rawAmenities : EMPTY_ARR;

  // recompute only when the underlying data actually changes,
  // not on every parent re-render (e.g. modal open/close of a sibling)
  const unitRanges = useMemo(
    () =>
      unit_size_ranges.map((u) => ({
        label: u.unit_type,
        value:
          u.min_size_sqft === u.max_size_sqft
            ? `${u.min_size_sqft} sqft`
            : `${u.min_size_sqft}-${u.max_size_sqft} sqft`,
      })),
    [unit_size_ranges]
  );

  const landmarkEntries = useMemo(
    () =>
      Object.entries(nearby_landmarks).filter(([, values]) => {
        const list = Array.isArray(values) ? values : values ? [values] : EMPTY_ARR;
        return list.length > 0;
      }),
    [nearby_landmarks]
  );

  const sizeRangeValue = useMemo(() => {
    if (!apartment_size_sqft?.min || !apartment_size_sqft?.max) return null;
    return `${apartment_size_sqft.min.toLocaleString()} – ${apartment_size_sqft.max.toLocaleString()} sq.ft`;
  }, [apartment_size_sqft?.min, apartment_size_sqft?.max]);

  if (!PropertyDetails) return null;

  return (
    <Modal
      className="property-details-modal w-90 "
      isOpen={isOpen}
      onClose={onClose}
      title="Property Details"
    >
      <div className="d-flex flex-column gap-4">
        {/* ── Overview stats ── */}
        <div className="overview-grid">
          <StatCard label="Size range" value={sizeRangeValue} />
          <StatCard label="Total homes" value={building_details?.total_units} />
          <StatCard label="Floors" value={building_details?.floors} />
          {building_details?.towers && (
            <StatCard label="Towers" value={building_details.towers} />
          )}
        </div>

        <Divider />

        <SectionTitle icon={LayoutTemplate}>Unit Ranges</SectionTitle>

        <div className="overview-grid">
          {unitRanges.map((unit) => (
            <StatCard key={unit.label} label={unit.label} value={unit.value} />
          ))}
        </div>

        {/* ── Highlights ── */}
        {highlights.length > 0 && (
          <div>
            <SectionTitle icon={Sparkles}>Highlights</SectionTitle>
            <div className="d-flex flex-wrap gap-1">
              {highlights.map((item, i) => (
                <span key={i} className="highlight-chip">
                  {toTitleCase(item)}
                </span>
              ))}
            </div>
          </div>
        )}

        {highlights.length > 0 && <Divider />}

        {amenities.length > 0 && (
          <PropertyDetailsAccordian
            title={`Amenities (${amenities.length})`}
            defaultOpen={false}
          >
            <div className="d-flex flex-wrap gap-1">
              {amenities.map((amenity, i) => {
                const { name, categories } = amenityFields(amenity);

                return (
                  <div
                    key={i}
                    className="amenity-chip d-inline-flex align-center gap-1"
                  >
                    <AmenityIcon categories={categories} name={name} />

                    {toTitleCase(name)}

                    {categories[0] && (
                      <span className="amenity-category">
                        · {toTitleCase(categories[0])}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </PropertyDetailsAccordian>
        )}

        {/* ── Nearby Landmarks ── */}
        <PropertyDetailsAccordian
          title={`Nearby Landmarks (${landmarkEntries.length})`}
          defaultOpen={false}
        >
          <div className="landmarks-grid">
            {landmarkEntries.map(([category, values]) => {
              const landmarkList = Array.isArray(values) ? values : [values];

              return (
                <div key={category} style={styles.card}>
                  <div className="landmark-header d-flex align-center gap-1">
                    <LandmarkIcon category={category} />
                    {category.replace(/_/g, " ")}
                  </div>

                  <div className="d-flex flex-column text-left whitespace-pre-line">
                    {landmarkList.map((item, idx) => {
                      const distance =
                        item?.distance_m != null
                          ? `${item.distance_m} m`
                          : item?.distance_km != null
                          ? `${item.distance_km} km`
                          : null;

                      return (
                        <div
                          key={idx}
                          className={`landmark-item ${
                            idx < landmarkList.length - 1 ? "landmark-item-bordered" : ""
                          }`}
                        >
                          {typeof item === "string" ? item : item?.name}
                          {distance && <span className="landmark-distance"> · {distance}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </PropertyDetailsAccordian>

        <Divider />

        {/* ── Approvals ── */}
        <div>
          <SectionTitle icon={BadgeCheck}>Approvals</SectionTitle>
          <div className="approvals-grid">
            {APPROVAL_FIELDS.map(({ label, key }) => (
              <div
                key={key}
                className="d-flex align-center gap-1"
                style={styles.card}
              >
                <ApprovalIcon value={approvals[key]} />
                <div>
                  <div style={styles.label}>{label}</div>
                  <div style={styles.valueSmall}>
                    {approvalText(approvals[key])}
                  </div>
                </div>
              </div>
            ))}

            {/* RERA spans full width */}
            <div className="approval-rera-card d-flex align-center gap-1">
              <Award size={18} style={RERA_LABEL_ICON_STYLE} />
              <div>
                <div style={styles.label}>RERA number</div>
                <div
                  style={
                    approvals.rera_registration_number
                      ? styles.valueSmall
                      : styles.valueSmallMuted
                  }
                >
                  {approvals.rera_registration_number || "Not available"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default React.memo(PropertyDetails);
